// Shared bits of the two big per-device clicker tables — the operations registry
// (clicker-operations-modal.ts) and the run history (clicker-history-modal.ts). Both
// are the same 90vw/90vh popup with a date range, a per-page picker and server-side
// paging, so the paging prefs, day-boundary maths, pills and cell formatting live here
// rather than in two copies free to drift.
import { esc } from '../ui/modal';
// Rows-per-page choices. Capped at 200 to match the server clamp on both endpoints.
// The choice is remembered per table (each passes its own storage key).
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
export const DEFAULT_PAGE_SIZE = 50;
export function loadPageSize(key) {
    const n = parseInt(localStorage.getItem(key) ?? '', 10);
    return PAGE_SIZE_OPTIONS.includes(n) ? n : DEFAULT_PAGE_SIZE;
}
export function savePageSize(key, n) {
    try {
        localStorage.setItem(key, String(n));
    }
    catch { /* best-effort */ }
}
/** `<option>` markup for the per-page picker, with `selected` on the current size. */
export function pageSizeOptionsHtml(selected) {
    return PAGE_SIZE_OPTIONS.map((n) => `<option value="${n}"${n === selected ? ' selected' : ''}>${n}</option>`).join('');
}
/**
 * A date-picker value (YYYY-MM-DD) → the instant that day starts in the VIEWER's timezone,
 * as a full ISO timestamp. `offsetDays` shifts by whole calendar days (1 = the exclusive
 * upper bound of the picked day).
 *
 * The rows print their time with toLocaleString(), i.e. in the viewer's timezone, so the
 * range has to be cut on local day boundaries. Sending the bare `YYYY-MM-DD` let Postgres
 * cast it at the SERVER's midnight, which shifted the whole range by the viewer's UTC
 * offset — a day filter quietly included the tail of the previous day and dropped the
 * matching tail of the picked one.
 */
export function dayBoundary(date, offsetDays = 0) {
    const d = new Date(`${date}T00:00:00`); // no trailing Z → parsed as local time
    if (Number.isNaN(d.getTime()))
        return null;
    d.setDate(d.getDate() + offsetDays);
    return d.toISOString();
}
/**
 * A date + an OPTIONAL time (`HH:MM`) → the range bound they name, in the viewer's
 * timezone, as a full ISO timestamp.
 *
 * The time box is additive on purpose: left empty, the pair means exactly what the date
 * picker alone always meant — the whole day, `end` giving the exclusive next midnight —
 * so nobody's saved habit of picking two dates changes meaning. Filled in, the bound is
 * the picked minute, and `end` extends it through that minute's last second, since the
 * picker cannot express anything finer.
 */
export function rangeBoundary(date, time, end = false) {
    if (!date)
        return null;
    if (!time)
        return dayBoundary(date, end ? 1 : 0);
    const d = new Date(`${date}T${time}:00`); // no trailing Z → parsed as local time
    if (Number.isNaN(d.getTime()))
        return null;
    if (end)
        d.setTime(d.getTime() + 60000);
    return d.toISOString();
}
export function fmtTime(iso) {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? esc(iso) : d.toLocaleString();
}
/** A value cell, or a muted em dash when the value is empty. */
export function dash(s) {
    return s ? esc(s) : '<span class="settings-hint">—</span>';
}
/** Colour-coded status pill (the `.ops-pill` family). Text is escaped. */
export function pill(text, kind) {
    return `<span class="ops-pill ops-pill--${kind}">${esc(text)}</span>`;
}
